import { SuitesComparison } from "@silversea-ssc/ui-components-react"
import { PricesResponseV2 } from "@ssc-booking/api-contracts"
import * as ROA from "fp-ts/ReadonlyArray"
import * as F from "fp-ts/function"
import React, { useMemo } from "react"

import { t, Trans } from "@lingui/macro"
import { totalDecks } from "../../app-core/features/suite/helpers"
import { useECommerceMachine } from "../../app-core/_common/machines/machines-context"
import { SanitySuitesComparisonModalQueryResponse } from "../../app-core/_common/sanity/sanity-types"
import { suiteCategories } from "../../app-core/features/_common/read-models/suite-category"
import { useGeolocation } from "../../app-core/_common/providers/geolocation-provider"
import { template, toComparableSuite } from "./utils"
import { SuitesComparisonSelectButton } from "./SuitesComparisonSelectButton"

type SuitesComparisonLoadedProps = {
    variant: "extended" | "compact"
    suitesContent: SanitySuitesComparisonModalQueryResponse
    allPrices: PricesResponseV2["prices"]
    targetSuiteCategory?: string
    SuitesComparisonSelectButton: SuitesComparisonSelectButton
}

const SuitesComparisonLoaded: React.FC<React.PropsWithChildren<SuitesComparisonLoadedProps>> = ({
    variant,
    suitesContent,
    allPrices,
    targetSuiteCategory,
    SuitesComparisonSelectButton,
}) => {
    const [state] = useECommerceMachine()
    const geoLocation = useGeolocation()

    const lastDeckNumber = totalDecks(suitesContent.version.decks)

    const comparableSuites = useMemo(
        () =>
            F.pipe(
                suiteCategories(suitesContent.version.suites, allPrices),
                ROA.map(toComparableSuite(geoLocation.unit, state.context.sanityContent.faresContent)),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(allPrices), JSON.stringify(suitesContent)],
    )

    const selectedSuiteCategory = state.context.selectedSuiteCategory

    const buildFooterButton = (suite: (typeof comparableSuites)[number]) => (
        <SuitesComparisonSelectButton
            suiteCategory={suite.suiteCategory}
            availability={suite?.availability ?? "standard"}
            selected={suite.suiteCategory == selectedSuiteCategory}
            full={variant == "compact"}
        />
    )

    return (
        <SuitesComparison
            variant={variant}
            numberOfComparableSuites={variant == "extended" ? 3 : 2}
            title={t`Compare categories`}
            body={
                variant == "extended" ? undefined : (
                    <Trans>
                        Want to know more about <strong>differences between suite categories?</strong>
                    </Trans>
                )
            }
            priceLabel={t`Total price`}
            template={template(lastDeckNumber)}
            suites={ROA.toArray(comparableSuites)}
            selectedSuiteCategory={targetSuiteCategory || selectedSuiteCategory}
            renderFooterButton={buildFooterButton}
            dataContentPiece="suites-comparison"
        />
    )
}

export default SuitesComparisonLoaded
